import { useRouter } from 'next/router';
import { isEqual } from 'lodash';

import { MediaQuery, useMediaQuery } from '../../../hooks/useMediaQuery';
import {
  FiltersModel,
  ProductCategoriesModel,
  ProductsModel
} from '../../../interface/products';
import { SUBZERO_SLUG } from '../../../constants/brands';
import ProductFilters from '../../../components/molecules/product-filters/product-filters';
import { useFilterProducts } from '../../../hooks/useFilterProducts';
import BookAnAppointment from '../../../components/molecules/book-an-appointment/book-an-appointment';
import ProductCard from '../../../components/molecules/product-card/product-card';
import SectionContainer from '../../../components/atoms/section-container/section-container';
import ProductsBanner from '../../../components/molecules/products-banner/products-banner';
import {
  getAllCategories,
  getCategoryDetails,
  getCategoryFilters,
  getProductsByCategory
} from '../../../services/products';
import Accordion from '../../../components/molecules/accordion/accordion';
import TabTitle from '../../../components/atoms/tab-title/tab-title';

type IndexProps = {
  categoryData: ProductCategoriesModel;
  categoryFilters: FiltersModel;
  products: ProductsModel[];
};

export function Index(props: IndexProps) {
  const { categoryData, categoryFilters, products } = props;

  const isTablet = useMediaQuery(MediaQuery.MD);
  const router = useRouter();
  function handleViewCategories() {
    router.push(`/${router.query.brand}`);
  }

  const { filteredProducts, handleClearFilters, handleSelectFilter } =
    useFilterProducts(products, categoryFilters);

  const accordionHeaderColor = isEqual(router.query.brand, SUBZERO_SLUG)
    ? 'text-primaryBlue'
    : 'text-primaryRed';

  const renderProducts = filteredProducts.map((product: ProductsModel) => (
    <ProductCard key={product.id} product={product} />
  ));

  const renderFilter = isTablet ? (
    <Accordion
      accordionHeaderColor={accordionHeaderColor}
      handleClearFilters={handleClearFilters}
      handleSelectFilter={handleSelectFilter}
      handleViewCategories={handleViewCategories}
    />
  ) : (
    <ProductFilters
      handleClearFilters={handleClearFilters}
      handleSelectFilter={handleSelectFilter}
      handleViewCategories={handleViewCategories}
    />
  );

  return (
    <>
      <TabTitle page={categoryData.attributes.categoryTitle} />
      <ProductsBanner
        bannerImage={categoryData.attributes.categoryImage}
        bannerDescription={categoryData.attributes.categoryDescription}
        bannerTitle={categoryData.attributes.categoryTitle}
      />
      <SectionContainer>
        <div className="grid grid-cols-12">
          <div className="col-span-12 md:col-span-2">{renderFilter}</div>
          <div className="col-span-12 mt-24 md:mt-0 md:col-span-10">
            <div className="flex flex-wrap justify-center md:justify-start gap-4 md:gap-6 md:pl-20">
              {renderProducts}
            </div>
          </div>
        </div>
      </SectionContainer>
      <BookAnAppointment />
    </>
  );
}

export async function getStaticProps({ params }: any) {
  const { category } = params;

  const categoryData = await getCategoryDetails(category);
  const products = await getProductsByCategory(category);
  const categoryFilters = await getCategoryFilters(
    categoryData.attributes.categoryTitle
  );

  return {
    props: { categoryData, categoryFilters, products },
    revalidate: 30
  };
}

export async function getStaticPaths() {
  const categories = await getAllCategories();

  const paths = categories.map((category) => {
    return {
      params: {
        brand: category.attributes.brand,
        category: category.attributes.categorySlug
      }
    };
  });

  return {
    paths,
    fallback: false
  };
}

export default Index;
